'use client';

import { createClient } from '@/lib/supabase/client';

export interface LeaderboardEntry {
  userId: string;
  fullName: string;
  email: string;
  totalPoints: number;
  reviewCount: number;
  totalHelpful: number;
  rank: number;
}

export interface ReviewPointEntry {
  id: string;
  userId: string;
  reviewId: string | null;
  points: number;
  reason: string;
  createdAt: string;
}

function mapLeaderboardRow(row: any): LeaderboardEntry {
  return {
    userId: row.user_id,
    fullName: row.full_name || 'Anonymous',
    email: row.email || '',
    totalPoints: Number(row.total_points) || 0,
    reviewCount: Number(row.review_count) || 0,
    totalHelpful: Number(row.total_helpful) || 0,
    rank: Number(row.rank) || 0,
  };
}

export async function fetchLeaderboard(limit = 50): Promise<LeaderboardEntry[]> {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('influencer_leaderboard')
      .select('*')
      .order('rank', { ascending: true })
      .limit(limit);
    if (error) { console.error('fetchLeaderboard error:', error.message); return []; }
    return (data || []).map(mapLeaderboardRow);
  } catch (err: any) {
    console.error('fetchLeaderboard exception:', err.message);
    return [];
  }
}

export async function fetchUserPoints(userId: string): Promise<{ total: number; history: ReviewPointEntry[] }> {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('review_points')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    if (error) { console.error('fetchUserPoints error:', error.message); return { total: 0, history: [] }; }
    const history: ReviewPointEntry[] = (data || []).map((r: any) => ({
      id: r.id,
      userId: r.user_id,
      reviewId: r.review_id,
      points: Number(r.points),
      reason: r.reason,
      createdAt: r.created_at,
    }));
    const total = history.reduce((sum, e) => sum + e.points, 0);
    return { total, history };
  } catch (err: any) {
    console.error('fetchUserPoints exception:', err.message);
    return { total: 0, history: [] };
  }
}

export async function awardPointsForReview(userId: string, reviewId: string): Promise<boolean> {
  const supabase = createClient();
  try {
    const { error } = await supabase
      .from('review_points')
      .insert({ user_id: userId, review_id: reviewId, points: 50, reason: 'Review submitted' });
    if (error) { console.error('awardPointsForReview error:', error.message); return false; }
    return true;
  } catch (err: any) {
    console.error('awardPointsForReview exception:', err.message);
    return false;
  }
}

export function subscribeToLeaderboard(onUpdate: () => void) {
  const supabase = createClient();
  const channel = supabase
    .channel('leaderboard-updates')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'review_points' }, onUpdate)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'college_reviews' }, onUpdate)
    .subscribe();
  return () => { supabase.removeChannel(channel); };
}
